import { useCallback, useEffect, useState } from "react";
import IAsset from "@/models/asset/Asset";
import { useSearchParams } from "react-router-dom";
import { useAssetBorrowingStore } from "@/store/asset_borrowing/assetBorrowingStore";
import { useAssetBorrowingNext } from "../AssetBorrowing";
import AssetSelect from "../../components/asset/AssetSelect";
import { useUserStore } from "@/store/user/userStore";
import Search from "@/components/search/Search";
import AssetBorrowingService from "@/lib/services/asset-borrowing/AssetBorrowingService";

const ChooseAssetItems = () => {
  const assetBorrowingStore = useAssetBorrowingStore();
  const { setAssetBorrowingNext } = useAssetBorrowingNext();
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState<IAsset[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [lastPage, setLastPage] = useState(1);
  const userStore = useUserStore();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentQuery, setCurrentQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);

  const getPage = useCallback(() => {
    const page = searchParams.get("page");
    return page ? +page : 1;
  }, [searchParams]);

  useEffect(() => {
    const selectedAssets = assetBorrowingStore.data.assets;
    if (selectedAssets !== undefined && selectedAssets.length > 0) {
      setAssetBorrowingNext(true);
    } else {
      setAssetBorrowingNext(false);
    }
  }, [assetBorrowingStore.data.assets]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    let ignore = false;

    const fetchData = async (p: number) => {
      setLoading(true);
      try {
        // OLD CODE:
        // const res = await AssetBorrowingService.getBorrowableAssetByRooms(
        //   userStore.data?.Email ?? "",
        //   "Student",
        //   p,
        //   [assetBorrowingStore.data.room],
        //   searchQuery.trim() ? searchQuery.trim() : undefined
        // );

        // NEW CODE: Pass selected dates and shifts so only available assets are returned
        const res = await AssetBorrowingService.getBorrowableAssetByRooms(
          userStore.data?.Email ?? "",
          "Student",
          p,
          [assetBorrowingStore.data.room],
          searchQuery.trim() ? searchQuery.trim() : undefined,
          assetBorrowingStore.data.pick_up_date,
          assetBorrowingStore.data.return_date,
          assetBorrowingStore.data.pick_up_shift ? Number(assetBorrowingStore.data.pick_up_shift.id) : undefined,
          assetBorrowingStore.data.return_shift ? Number(assetBorrowingStore.data.return_shift.id) : undefined
        );

        if (ignore) return;

        setLastPage(res.last_page ?? 1);
        setAssets(res.data);
        setCurrentPage(p);
        setCurrentQuery(searchQuery);
      } catch (error) {
        if (!signal.aborted) {
          console.error(error);
          setAssets([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    const page = getPage();

    if (assetBorrowingStore.data.room !== -1 && !ignore) {
      if (
        assets.length <= 1 ||
        page !== currentPage ||
        currentQuery != searchQuery
      ) {
        fetchData(page);
      }
    }

    return () => {
      ignore = true;
      controller.abort();
    };
  }, [
    assetBorrowingStore.data.room,
    assetBorrowingStore.data.pick_up_date,
    assetBorrowingStore.data.return_date,
    assetBorrowingStore.data.pick_up_shift,
    assetBorrowingStore.data.return_shift,
    searchQuery,
    assets.length,
    currentPage,
    currentQuery,
    userStore.data?.Email,
    getPage
  ]);

  const handleSelect = (asset: IAsset) => {
    const selected = assetBorrowingStore.data.assets;
    const isSelected = selected.some((a) => a.id === asset.id);
    const newSelection = isSelected
      ? selected.filter((a) => a.id !== asset.id)
      : [...selected, asset];
    assetBorrowingStore.update("assets", newSelection);
  };

  const changePage = (page: number) => {
    if (page < 1 || page > lastPage) return;
    setSearchParams({ page: page + "" });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  return (
    <div className={`w-full`}>
      {!loading && (
        <Search
          containerClassName="py-4"
          onSearch={handleSearch}
          placeHolder="Search Asset Name"
        />
      )}
      <AssetSelect
        assets={assets}
        loading={loading}
        handleSelect={handleSelect}
        currentPage={getPage()}
        lastPage={lastPage}
        changePage={changePage}
        title="Asset"
      />
    </div>
  );
};

export default ChooseAssetItems;
